import React, { useCallback, useMemo } from 'react';

import { MultiCellConfigItem } from '../types';
import { ConfigItemElementProps } from './types';
import { buildContextSelectorRows, buildEntityFromSelectedRows } from '../utils/helper';
import { ContextSelector, TableRow, SelectedRows, Tab, ColumnConfiguration } from '../../context-selector-v2';

const columnConfig: ColumnConfiguration[] = [
  { columnKey: 'productGroup', columnName: 'Product Group', isSortingEnabled: true },
  { columnKey: 'country', columnName: 'Country', isSortingEnabled: true },
  { columnKey: 'latestMonth', columnName: 'Latest Month', isSortingEnabled: true },
  { columnKey: 'latestWeek', columnName: 'Latest Week', isSortingEnabled: true },
];
const tabs: Tab[] = [
  {
    value: 'default',
    label: 'All markets',
    searchPlaceHolder: 'Search all markets',
  },
  {
    value: 'country',
    label: 'Countries',
    hasSeperator: true,
    showGroupedTablesByValue: true,
    searchPlaceHolder: 'Search product groups',
  },
  {
    value: 'productGroup',
    label: 'Product Groups',
    showGroupedTablesByValue: true,
    searchPlaceHolder: 'Search countries',
  },
];

/**
 * MultiCellElement component renders a MultiCellSelector with the provided configuration.
 *
 * @param {ConfigItemElementProps<MultiCellConfigItem>} props - The properties for the component.
 * @param {MultiCellConfigItem} props.configItem - The configuration item containing key, options, and actual value.
 * @param {string} props.configItem.key - The unique key for the configuration item.
 * @param {Entity[]} props.configItem.options - The available multiple cells for selection.
 * @param {Entity[]} props.configItem.value - The currently selected cells.
 * @param {function} props.onChange - The callback function to handle changes in selection.
 * @returns {JSX.Element} The rendered MultiCellSelector component.
 */
function MultiCellElement({
  configItem: { key, options, value: actual },
  onChange,
}: ConfigItemElementProps<MultiCellConfigItem>): JSX.Element {
  const handleOnApply = useCallback<(selected: TableRow[]) => void>(
    (selected: TableRow[]) => {
      onChange(key, buildEntityFromSelectedRows(selected));
    },
    [key, onChange]
  );

  const data = buildContextSelectorRows(options);

  const initialSelectedRows = useMemo<SelectedRows>(() => {
    if (Array.isArray(actual) && actual.length) {
      return Object.fromEntries(actual.map(r => [r.value, true]));
    } else {
      return {} as SelectedRows;
    }
  }, [actual]);

  // Render the component.
  return (
    <ContextSelector
      tableInfo={{ data, columnConfig }}
      tabs={tabs}
      triggerText="Select your market(s)"
      selectionSectionHeader="Selected markets"
      onView={handleOnApply}
      initialSelectedRows={initialSelectedRows}
    />
  );
}

export { MultiCellElement };
